import {
  ListSubheader,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { VariableSizeList, ListChildComponentProps } from "react-window"
import React from "react"

const LISTBOX_PADDING = 12 // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props
  const dataSet = data[index]
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  }

  if (dataSet.hasOwnProperty("group")) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    )
  }

  return (
    <Typography
      component="li"
      {...dataSet[0]}
      key={dataSet.key ?? index}
      noWrap
      style={inlineStyle}
    >
      {`${dataSet[1]}`}
    </Typography>
  )
}

const OuterElementContext = React.createContext<
  React.HTMLAttributes<HTMLElement>
>({})

const OuterElementType = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext)
  return (
    <div
      ref={ref}
      {...props}
      {...outerProps}
      onScroll={(event) => {
        props.onScroll?.(event)
        outerProps.onScroll?.(event)
      }}
    />
  )
})

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null)
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true)
    }
  }, [data])
  return ref
}

interface ListboxComponentProps extends React.HTMLAttributes<HTMLElement> {
  isLoading?: boolean
}

export const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  ListboxComponentProps
>(function ListboxComponent(props, ref) {
  const { children, isLoading = false, ...other } = props
  const itemData: React.ElementType[] = []
  ;(children as React.ElementType[]).forEach(
    (item: React.ElementType & { children?: React.ElementType[] }) => {
      itemData.push(item)
      itemData.push(...(item.children || []))
    },
  )

  const theme = useTheme()
  const smUp = useMediaQuery(theme.breakpoints.up("sm"), {
    noSsr: true,
  })
  const itemCount = itemData.length
  const itemSize = smUp ? 36 : 48

  const getChildSize = (child: React.ElementType) => {
    if (child.hasOwnProperty("group")) {
      return 48
    }

    return itemSize
  }

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
  }

  const gridRef = useResetCache(itemCount)

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
      {isLoading && itemCount > 0 && (
        <Typography
          role="status"
          color="text.secondary"
          sx={{ px: 2, py: 1 }}
          onMouseDown={(event) => event.preventDefault()}
        >
          Загрузка…
        </Typography>
      )}
    </div>
  )
})
